import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/analysis`;
const PATHS = { yoga: 'w37-yoga', sameDate: 'w37-same-date-repeat' };
const initial = () => ({ data: null, loading: true, error: null, invalidPoseCount: 0 });

// Each endpoint owns its state: an optional tab must never discard pose data.
export const useW37Data = () => {
  const [resources, setResources] = useState(() => ({ yoga: initial(), sameDate: initial() }));
  const pending = useRef({});
  const load = useCallback((key) => {
    if (pending.current[key]) return pending.current[key].promise;
    const controller = new AbortController();
    const update = (values) => {
      if (!controller.signal.aborted) {
        setResources(previous => ({ ...previous, [key]: { ...previous[key], ...values } }));
      }
    };
    update({ loading: true });
    const promise = axios.get(`${API}/${PATHS[key]}`, {
      withCredentials: true, signal: controller.signal, timeout: 45000,
    }).then(({ data }) => {
      if (!data || typeof data !== 'object' || Array.isArray(data)
          || (key === 'yoga' && (!data.poses || typeof data.poses !== 'object' || Array.isArray(data.poses)))) {
        throw new Error('Invalid W37 response');
      }
      if (key === 'yoga') {
        const entries = Object.entries(data.poses);
        const valid = entries.filter(([, pose]) => pose && typeof pose === 'object'
          && !Array.isArray(pose) && typeof pose.name === 'string');
        update({ data: { ...data, poses: Object.fromEntries(valid) }, error: null, invalidPoseCount: entries.length - valid.length });
      } else {
        update({ data, error: null });
      }
    }).catch(() => {
      update({ error: key === 'yoga' ? 'Pose Summary could not be loaded.' : 'Same Date Repeat could not be loaded.' });
    }).finally(() => {
      update({ loading: false });
      if (pending.current[key]?.controller === controller) delete pending.current[key];
    });
    pending.current[key] = { controller, promise };
    return promise;
  }, []);

  useEffect(() => {
    const active = pending.current;
    return () => {
      Object.keys(active).forEach(key => {
        active[key].controller.abort();
        delete active[key];
      });
    };
  }, []);

  const fetchData = useCallback(() => Promise.allSettled([load('yoga'), load('sameDate')]), [load]);
  const retryYoga = useCallback(() => load('yoga'), [load]);
  const retrySameDate = useCallback(() => load('sameDate'), [load]);
  return { ...resources, fetchData, retryYoga, retrySameDate };
};
